<script setup>
import { ref, watch, onMounted } from 'vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useContactLabels } from 'dashboard/composables/useContactLabels';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Spinner from 'shared/components/Spinner.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import AddLabel from 'shared/components/ui/dropdown/AddLabel.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    default: null,
  },
});

const store = useStore();
const { isAdmin } = useAdmin();

const {
  selectedLabelTitles,
  activeLabels,
  accountLabels,
  updatingLabelTitles,
  addLabelToContact,
  removeLabelFromContact,
  fetchContactLabels,
} = useContactLabels(() => props.contactId);

const contactLabelsUiFlags = useMapGetter('contactLabels/getUIFlags');

const showSearchDropdownLabel = ref(false);

const toggleLabels = () => {
  showSearchDropdownLabel.value = !showSearchDropdownLabel.value;
};

const closeDropdownLabel = () => {
  showSearchDropdownLabel.value = false;
};

watch(
  () => props.contactId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      fetchContactLabels();
    }
  }
);

onMounted(() => {
  store.dispatch('labels/get');
  fetchContactLabels();
});
</script>

<template>
  <div class="sidebar-labels-wrap">
    <div
      v-if="!contactLabelsUiFlags.isFetching"
      class="contact-conversation--list"
    >
      <div
        v-on-clickaway="closeDropdownLabel"
        class="label-wrap flex flex-wrap"
        @keyup.esc="closeDropdownLabel"
      >
        <AddLabel @add="toggleLabels" />
        <woot-label
          v-for="label in activeLabels"
          :key="label.id"
          :title="label.title"
          :description="label.description"
          show-close
          :color="label.color"
          variant="smooth"
          class="max-w-[calc(100%-0.5rem)]"
          :is-updating="updatingLabelTitles.includes(label.title)"
          @remove="removeLabelFromContact"
        />

        <div
          :class="{
            'block visible': showSearchDropdownLabel,
            'hidden invisible': !showSearchDropdownLabel,
          }"
          class="border rounded-lg bg-n-alpha-3 top-6 backdrop-blur-[100px] absolute w-full shadow-lg border-n-strong dark:border-n-strong p-2 box-border z-[9999]"
        >
          <LabelDropdown
            v-if="showSearchDropdownLabel"
            :account-labels="accountLabels"
            :selected-labels="selectedLabelTitles"
            :pending-labels="updatingLabelTitles"
            :allow-creation="isAdmin"
            @add="addLabelToContact"
            @remove="removeLabelFromContact"
          />
        </div>
      </div>
    </div>
    <Spinner v-else />
  </div>
</template>

<style lang="scss" scoped>
.sidebar-labels-wrap {
  margin-bottom: 0;
}
.contact-conversation--list {
  width: 100%;

  .label-wrap {
    line-height: 1.5rem;
    position: relative;
  }
}
</style>
